import React, { useEffect, useMemo, useRef, useState } from "react";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import Pagination from "../../common/TablePagination";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../common/Loader";
import SearchBar from "../../common/SearchBar";
import { ReactComponent as DeleteIcon } from "../../../Assets/Icons/deleteIcon.svg";
import {
  getUsers,
  getUserById,
  deleteUser,
  updateUserStatus,
} from "../../../services/adminService";
import debounce from "lodash.debounce";
import DetailsModal from "../../common/DetailsModal";
import PopupContent from "./PopupContent";
import { Dropdown } from "antd";
import FilterPanel from "../../common/FilterPanel";
import { DatePicker } from "antd";
import toast from "../../../utils/toast";
import CancelPopup from "../../common/CancelPopup";
import { ReactComponent as FiltersIcon } from "../../../Assets/Icons/filtersIcon.svg";
// Removed fundraiser and merchant filters from Member List
import RedeemedOffers from "./RedeemedOffers";
import FilterButton from "../../common/FilterButton";
import ActiveFilterBar from "../../common/ActiveFilterBar";
import { Drawer as MuiDrawer } from "@mui/material";
import TableEmptyState from "../../common/TableEmptyState";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import { formatCustomDateRangeLabel } from "../../../utils/filterLabelUtils";
import TruncatedTableCell from "../../common/TruncatedTableCell";
import { matchesListDateRange } from "../../../utils/listTimePeriod";

const MemberListDataTable = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.adminReducer?.loading);
  const [allMembers, setAllMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [statusToggled, setStatusToggled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  // Removed fundraiser and merchant search queries for Member List
  const [open, setOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [memberDetails, setMemberDetails] = useState({});
  const [searching, setSearching] = useState(false);
  // Removed fundraiser and merchant lists for Member List
  const [filterParams, setFilterParams] = useState({
    status: null,
    duration: null,
    startDate: null,
    endDate: null,
  });

  const DURATION_MAP = {
    Today: "DAY",
    "This Week": "WEEK",
    "This Month": "MONTH",
    "This Year": "YEAR",
    "Custom Date": "CUSTOM",
  };
  const [customDate, setCustomDate] = useState([null, null]);

  // Redeemed offer modal state
  const [offerDetailsOpen, setOfferDetailsOpen] = useState(false);
  const [offerDetails, setOfferDetails] = useState(null);

  // Delete confirm modal state (Member)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);
  const [statusChangeTargetMember, setStatusChangeTargetMember] =
    useState(null);
  const [openMemberStatusDropdownId, setOpenMemberStatusDropdownId] =
    useState(null);
  const statusMutationLockRef = useRef(false);

  // Removed fundraiser and merchant debounced searches for Member List

  // Removed fundraiser and merchant search handlers

  // Convert filterData to a computed value instead of state
  const filterData = useMemo(
    () => [
      {
        id: "subStatus",
        title: "Subscription Status",
        options: ["Active", "Expired", "Not Subscribed"],
        multiSelect: false,
        valueMap: {
          Active: "active",
          Expired: "expired",
          "Not Subscribed": "not_subscribed",
        },
      },
      {
        id: "memberStatus",
        title: "Member Status",
        options: ["Live", "Disabled", "Deleted"],
        multiSelect: false,
        valueMap: {
          Live: "live",
          Disabled: "disabled",
          Deleted: "deleted",
        },
      },
      {
        id: "duration",
        title: "Time Period",
        options: [
          "Today",
          "This Week",
          "This Month",
          "This Year",
          "Custom Date",
        ],
        multiSelect: false,
        valueMap: DURATION_MAP,
        customContent: (selected) => {
          return selected === "Custom Date" ? (
            <div className="mt-3 flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-medium text-gray-600">
                  Start Date
                </label>
                <DatePicker
                  value={customDate?.[0]}
                  onChange={(date) => {
                    const newDates = [date, customDate?.[1]];
                    setCustomDate(newDates);
                  }}
                  style={{ width: "100%", pointerEvents: "auto" }}
                  placeholder="Start Date"
                  format="YYYY-MM-DD"
                  getPopupContainer={(trigger) => trigger.parentNode}
                  popupClassName="date-picker-popup"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-medium text-gray-600">
                  End Date
                </label>
                <DatePicker
                  value={customDate?.[1]}
                  onChange={(date) => {
                    const newDates = [customDate?.[0], date];
                    setCustomDate(newDates);
                  }}
                  style={{ width: "100%", pointerEvents: "auto" }}
                  placeholder="End Date"
                  format="YYYY-MM-DD"
                  getPopupContainer={(trigger) => trigger.parentNode}
                  popupClassName="date-picker-popup"
                />
              </div>
            </div>
          ) : null;
        },
      },
    ],
    [customDate],
  );

  // Create initial state from filter data
  const createInitialState = (filters) => {
    return filters.reduce((acc, filter) => {
      if (filter.multiSelect) {
        acc[filter.id] = Array.isArray(filter.defaultValue)
          ? filter.defaultValue
          : [];
      } else {
        acc[filter.id] = filter.defaultValue;
      }
      return acc;
    }, {});
  };

  const [selections, setSelections] = useState(createInitialState(filterData));

  const [fullMembers, setFullMembers] = useState([]);

  // Client-side pagination helper
  const updatePageSlice = (data, page) => {
    if (!data) return;
    const startIndex = (page - 1) * 10;
    const endIndex = startIndex + 10;
    setAllMembers(data.slice(startIndex, endIndex));
  };

  const normalizeSearchText = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "");

  const filteredMembers = useMemo(() => {
    let list = Array.isArray(fullMembers) ? [...fullMembers] : [];
    const q = normalizeSearchText(searchQuery);

    list = list.filter((member) =>
      matchesListDateRange(
        member,
        filterParams.duration === "CUSTOM" ? filterParams.startDate : null,
        filterParams.duration === "CUSTOM" ? filterParams.endDate : null,
        ["createdAt", "created_at", "updatedAt", "updated_at"],
      ),
    );

    if (filterParams.subStatus) {
      list = list.filter(
        (member) =>
          String(
            member?.subscriptionStatus || member?.subStatus || "",
          ).toLowerCase() === String(filterParams.subStatus).toLowerCase(),
      );
    }

    if (filterParams.memberStatus) {
      list = list.filter(
        (member) =>
          String(member?.status || "").toLowerCase() ===
          String(filterParams.memberStatus).toLowerCase(),
      );
    }

    if (q) {
      list = list.filter((member) => {
        const name = normalizeSearchText(member?.user?.name || member?.name);
        const email = normalizeSearchText(member?.user?.email || member?.email);
        const subscription = normalizeSearchText(
          member?.subscriptionStatus || member?.subStatus,
        );
        const memberStatus = normalizeSearchText(member?.status);
        return (
          name.includes(q) ||
          email.includes(q) ||
          subscription.includes(q) ||
          memberStatus.includes(q)
        );
      });
    }

    return list;
  }, [fullMembers, searchQuery, filterParams]);

  const getMembersData = async (
    query = "",
    silent = false,
    filterOverride = null,
  ) => {
    if (silent) setSearching(true);
    const activeFilters = filterOverride || filterParams;
    const isCustomDuration = activeFilters?.duration === "CUSTOM";
    const requestStartDate =
      isCustomDuration && customDate?.[0]
        ? customDate[0].format("YYYY-MM-DD")
        : activeFilters?.startDate || null;
    const requestEndDate =
      isCustomDuration && customDate?.[1]
        ? customDate[1].format("YYYY-MM-DD")
        : activeFilters?.endDate || null;
    const payload = {
      limit: 500,
      ...(activeFilters?.duration && { duration: activeFilters.duration }),
      // Send the backend-supported member and subscription filters explicitly.
      status: activeFilters?.memberStatus || undefined,
      subscriptionStatus: activeFilters?.subStatus || undefined,
      ...(requestStartDate && { startDate: requestStartDate }),
      ...(requestEndDate && { endDate: requestEndDate }),
    };
    // Remove undefined values so no empty params are sent
    Object.keys(payload).forEach(
      (k) => payload[k] === undefined && delete payload[k],
    );

    try {
      const membersPromise = await dispatch(getUsers(payload))?.unwrap();
      if (membersPromise?.success === true) {
        setFullMembers(membersPromise?.data || []);
      }
    } finally {
      setSearching(false);
    }
  };

  // 1. Initial/Filter Load
  useEffect(() => {
    getMembersData("", false);
  }, [statusToggled]);

  // 2. Logic to filter and slice data for display
  useEffect(() => {
    if (!filteredMembers) return;

    const nextTotalPages = Math.ceil(filteredMembers.length / 10) || 1;
    const nextPage = Math.min(currentPage, nextTotalPages);

    setCount(filteredMembers.length);
    setTotalPages(nextTotalPages);

    if (nextPage !== currentPage) {
      setCurrentPage(nextPage);
    }

    updatePageSlice(filteredMembers, nextPage);
  }, [filteredMembers, currentPage]);

  const items = [{ title: "Members" }, { title: "Member List" }];

  const columns = [
    { name: "No." },
    { name: "Name" },
    { name: "Date Joined" },
    { name: "Email" },
    { name: "Sub. Amount" },
    { name: "Subscription" },
    { name: "Member Status" },
    { name: "Actions" },
  ];

  const renderMemberChevron = () => (
    <svg width="7" height="4" viewBox="0 0 10 6" fill="none">
      <path
        d="M1 1.5L5 5.5L9 1.5"
        stroke="#303033"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const getSubscriptionBadgeMeta = (subscriptionStatus) => {
    const normalized = String(subscriptionStatus || "").toLowerCase();

    if (normalized === "active" || subscriptionStatus === true) {
      return {
        label: "Active",
        bgColor: "rgba(80, 205, 137, 0.15)",
        borderColor: "#50CD89",
        dotColor: "#50CD89",
        icon: (
          <svg
            width="10"
            height="10"
            viewBox="8 6 14 14"
            fill="none"
            style={{ display: "block" }}
          >
            <path
              d="M12 13.6667L14.3333 16L19 11"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      };
    }

    if (normalized === "not_subscribed") {
      return {
        label: "Not Subscribed",
        bgColor: "rgba(140, 140, 140, 0.12)",
        borderColor: "#8C8C8C",
        dotColor: "#8C8C8C",
        icon: (
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3.5"
            strokeLinecap="round"
            style={{ display: "block" }}
          >
            <path d="M7 12h10" />
          </svg>
        ),
      };
    }

    return {
      label: "Expired",
      bgColor: "rgba(236, 130, 110, 0.15)",
      borderColor: "#EC826E",
      dotColor: "#EC826E",
      icon: (
        <svg
          width="10"
          height="10"
          viewBox="8 6 14 14"
          fill="none"
          style={{ display: "block" }}
        >
          <path
            d="M18 15.8573C18 15.9358 17.9377 16 17.8616 16L16.7197 15.9946L15 13.8811L13.282 15.9929L12.1384 15.9982C12.0623 15.9982 12 15.9358 12 15.8555C12 15.8216 12.0121 15.7895 12.0329 15.7628L14.2837 12.9982L12.0329 10.2354C12.012 10.2093 12.0004 10.1766 12 10.1427C12 10.0642 12.0623 10 12.1384 10L13.282 10.0054L15 12.1189L16.718 10.0071L17.8599 10.0018C17.936 10.0018 17.9983 10.0642 17.9983 10.1445C17.9983 10.1784 17.9862 10.2105 17.9654 10.2372L15.718 13L17.9689 15.7646C17.9896 15.7895 18 15.8234 18 15.8573Z"
            fill="white"
          />
        </svg>
      ),
    };
  };

  const handleStatusChange = async (memberId, newStatus) => {
    if (statusChangeLoading || statusMutationLockRef.current) return false;
    statusMutationLockRef.current = true;
    setStatusChangeLoading(true);
    try {
      const action =
        newStatus === "disabled"
          ? deleteUser({ id: memberId, status: "disabled" })
          : updateUserStatus({ id: memberId, status: newStatus });
      const result = await dispatch(action).unwrap();
      if (result) {
        getMembersData(searchQuery, true); // Re-fetch data silently to update status
        if (isDetailOpen && memberDetails?._id === memberId) {
          fetchMemberDetails(memberId);
        }
        toast.success(
          `Member status changed to ${newStatus === "disabled" ? "Disabled" : "Live"} successfully!`,
        );
        return true;
      }
    } catch (error) {
      console.error("Failed to update member status:", error);
      toast.error(`Failed to change member status to ${newStatus}.`);
      return false;
    } finally {
      setStatusChangeLoading(false);
      statusMutationLockRef.current = false;
    }
  };

  const handleStatusChangeRequest = (memberId, newStatus, member = null) => {
    if (statusChangeLoading || statusMutationLockRef.current) return;
    setTargetStatus(newStatus);
    setStatusChangeTargetMember(
      member ||
        allMembers?.find(
          (item) => item?._id === memberId || item?.id === memberId,
        ) ||
        null,
    );
    setShowStatusConfirm(true);
  };

  const handleStatusConfirm = async () => {
    if (statusChangeLoading || statusMutationLockRef.current) return;
    const memberId =
      statusChangeTargetMember?._id || statusChangeTargetMember?.id;
    if (!memberId || !targetStatus) {
      setShowStatusConfirm(false);
      setStatusChangeTargetMember(null);
      setTargetStatus(null);
      return;
    }

    try {
      const updated = await handleStatusChange(memberId, targetStatus);
      if (!updated) return;
      setIsDetailOpen(false);
      setMemberDetails(null);
      setShowStatusConfirm(false);
      setStatusChangeTargetMember(null);
      setTargetStatus(null);
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const handleStatusCancel = () => {
    if (statusChangeLoading || statusMutationLockRef.current) return;
    setShowStatusConfirm(false);
    setStatusChangeTargetMember(null);
    setTargetStatus(null);
  };

  const handleDeleteMember = (memberId) => {
    setMemberToDelete(memberId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!memberToDelete) return;
    try {
      const result = await dispatch(
        deleteUser({ id: memberToDelete, status: "deleted" }),
      )?.unwrap();
      if (result) {
        setIsDetailOpen(false);
        getMembersData(searchQuery, true);
        toast.success("Member deleted successfully!");
      } else {
        toast.error("Failed to delete member.");
      }
    } catch (error) {
      console.error("Failed to delete member:", error);
      toast.error(error?.message || "Failed to delete member.");
    } finally {
      setShowDeleteConfirm(false);
      setMemberToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setMemberToDelete(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const truncateMemberName = (value, maxLength = 18) => {
    if (!value) return "N/A";
    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of member list when changing pages
    const container = document.getElementById("member-list-container");
    if (container) {
      container.scrollIntoView({ behavior: "smooth" });
    } else {
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value);
        setCurrentPage(1);
        setSearching(false);
      }, 500),
    [],
  );

  const handleSearchChange = (value) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  // Removed cleanup for fundraiser and merchant debounced searches

  const fetchMemberDetails = async (memberId) => {
    try {
      const result = await dispatch(
        getUserById({ id: memberId, silent: true }),
      )?.unwrap();
      if (result?.success) {
        setMemberDetails(result.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const openPopup = async (memberId) => {
    setMemberDetails(null);
    setIsDetailOpen(true);
    if (memberId) {
      fetchMemberDetails(memberId);
    }
  };

  const closePopup = () => {
    setMemberDetails(null);
    setIsDetailOpen(false);
  };

  // Removed fundraiser and merchant data fetching and effects for User List

  const activeFilterChips = useMemo(() => {
    const chips = [];
    Object.entries(filterParams).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      )
        return;
      if (value === "") return;

      const filter = filterData.find((f) => f.id === key);
      if (!filter) return;

      if (filter.multiSelect && Array.isArray(value)) {
        value.forEach((val) => {
          const displayLabel =
            Object.keys(filter.valueMap || {}).find(
              (k) => filter.valueMap[k] === val,
            ) || val;
          chips.push({ id: key, value: val, label: `${displayLabel}` });
        });
      } else {
        const displayLabel =
          Object.keys(filter.valueMap || {}).find(
            (k) => filter.valueMap[k] === value,
          ) || value;
        chips.push({ id: key, value: value, label: `${displayLabel}` });
      }
    });

    // Special handling for custom date range label
    if (filterParams.duration === "CUSTOM") {
      const dChip = chips.find((c) => c.id === "duration");
      if (dChip) {
        dChip.label = formatCustomDateRangeLabel(
          filterParams.startDate || customDate?.[0],
          filterParams.endDate || customDate?.[1],
        );
      }
    }

    return chips;
  }, [filterParams, filterData, customDate]);

  const activeCount = activeFilterChips.length;
  const isMemberCustomDateIncomplete =
    selections?.duration === "Custom Date" &&
    (!customDate?.[0] || !customDate?.[1]);

  const handleSelectionChange = (filterId, value) => {
    setSelections((prev) => ({
      ...prev,
      [filterId]: value,
    }));

    if (filterId === "duration" && value !== "CUSTOM") {
      setCustomDate([null, null]);
    }
  };

  const applyFilters = (sels) => {
    const mappedFilterParams = {
      status: null,
      duration: null,
      startDate: null,
      endDate: null,
    };
    filterData.forEach((filter) => {
      const selectedValue = sels[filter.id];
      if (
        selectedValue !== null &&
        selectedValue !== undefined &&
        selectedValue !== "" &&
        !(Array.isArray(selectedValue) && selectedValue.length === 0)
      ) {
        if (
          filter.id === "duration" &&
          selectedValue === "CUSTOM" &&
          customDate &&
          (customDate[0] || customDate[1])
        ) {
          mappedFilterParams.duration = "CUSTOM";
          mappedFilterParams.startDate =
            customDate[0]?.format("YYYY-MM-DD") || null;
          mappedFilterParams.endDate =
            customDate[1]?.format("YYYY-MM-DD") || null;
        } else if (filter.valueMap) {
          mappedFilterParams[filter.id] = filter.multiSelect
            ? selectedValue.map((val) => filter.valueMap[val])
            : filter.valueMap[selectedValue];
        } else {
          mappedFilterParams[filter.id] = selectedValue;
        }
      }
    });
    setFilterParams(mappedFilterParams);
    setCurrentPage(1);
    return mappedFilterParams;
  };

  const handleApply = () => {
    const mappedFilterParams = applyFilters(selections);
    getMembersData(searchQuery, false, mappedFilterParams);
    setOpen(false);
  };

  const handleReset = () => {
    const initialState = createInitialState(filterData);
    const clearedFilterParams = {
      status: null,
      duration: null,
      startDate: null,
      endDate: null,
    };
    setSelections(initialState);
    setFilterParams(clearedFilterParams);
    setCustomDate([null, null]);
    setSearchInput("");
    setSearchQuery("");
    setCurrentPage(1);
    getMembersData("", false, clearedFilterParams);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOpenChange = (flag) => {
    setOpen(flag);
  };

  const handleRemoveFilter = (targetChip) => {
    const filter = filterData.find((f) => f.id === targetChip.id);
    if (!filter) return;

    let newValue;
    if (filter.multiSelect) {
      newValue = selections[targetChip.id].filter(
        (v) => v !== targetChip.value,
      );
    } else {
      newValue = null;
    }

    const newSelections = { ...selections, [targetChip.id]: newValue };
    setSelections(newSelections);

    if (targetChip.id === "duration" && newValue === null) {
      setCustomDate([null, null]);
    }

    const mappedFilterParams = applyFilters(newSelections);
    getMembersData(searchQuery, false, mappedFilterParams);
  };

  // Callback for PopupContent
  const handleRedeemedOfferClick = (offer) => {
    setIsDetailOpen(false); // Close member modal
    setOfferDetails(offer);
    setOfferDetailsOpen(true); // Open offer details modal
  };

  return (
    <>
      {loading && <Loader />}
      {isDetailOpen && (
        <DetailsModal
          handleClose={closePopup}
          content={
            <PopupContent
              memberDetails={memberDetails}
              onRedeemedOfferClick={handleRedeemedOfferClick}
              onStatusChangeRequest={handleStatusChangeRequest}
              onDelete={handleDeleteMember}
            />
          }
          popupClassName="p-6 w-[95%] max-w-[95vw] sm:w-[600px] md:w-[650px]"
        />
      )}
      {offerDetailsOpen && (
        <DetailsModal
          handleClose={() => {
            setOfferDetailsOpen(false);
            setIsDetailOpen(true); // Re-open parent modal
          }}
          content={<RedeemedOffers offerDetails={offerDetails} />}
          popupClassName="p-6 w-[95%] max-w-[95vw] sm:w-[600px] md:w-[600px]"
        />
      )}
      {showDeleteConfirm && (
        <DeleteConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          itemName="Member"
        />
      )}
      {showStatusConfirm && (
        <CancelPopup
          handleNo={handleStatusCancel}
          handleYes={handleStatusConfirm}
          heading="Change Status"
          content={
            String(targetStatus || "").toLowerCase() === "deleted"
              ? "Are you sure you want to delete this member? This action cannot be undone."
              : String(targetStatus || "").toLowerCase() === "live"
                ? "Are you sure you want to make this member Live?"
                : "Are you sure you want to disable this member?"
          }
          confirmation={String(targetStatus || "").toLowerCase() === "live"}
          popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
          confirmDisabled={statusChangeLoading}
          cancelDisabled={statusChangeLoading}
          confirmLabel={statusChangeLoading ? "Updating..." : "Confirm"}
        />
      )}
      <div className="flex flex-col sm:flex-row justify-between col-span-12 gap-4">
        <BreadCrumsHeader heading="Member List" items={items} />
      </div>
      <div className="w-full flex justify-between items-center mt-6 gap-3">
        <SearchBar
          parentDivClass="w-full sm:w-[60%] md:w-[40%] lg:w-[35%] xl:w-[30%]"
          divClassName="relative w-full"
          inputClassName="searchBar w-full p-3 pr-10 text-[14px] font-poppins font-[400] outline-none rounded-[5px] border border-[#D9D9D9] focus:border-[#00ADEF]"
          placeholder="Search"
          value={searchInput}
          handleChange={(e) => handleSearchChange(e.target.value)}
          onClear={() => handleSearchChange("")}
          loading={searching}
        />
        <div className="flex items-center gap-2">
          <FilterButton
            activeCount={activeCount}
            isOpen={open}
            onClick={() => setOpen(true)}
          />
        </div>
        <MuiDrawer
          anchor="right"
          open={open}
          onClose={() => setOpen(false)}
          ModalProps={{ keepMounted: true }}
          TransitionComponent={Slide}
          SlideProps={{
            appear: true,
            timeout: { enter: 700, exit: 500 },
            easing: {
              enter: "cubic-bezier(0.22, 1, 0.36, 1)",
              exit: "cubic-bezier(0.4, 0, 0.2, 1)",
            },
          }}
          PaperProps={{
            sx: {
              width: { xs: "100%", sm: "400px" },
              padding: "0",
              height: "100%",
            },
          }}
        >
          <FilterPanel
            filterData={filterData}
            selections={selections}
            appliedSelections={filterParams}
            handleApply={handleApply}
            handleCancel={handleCancel}
            handleReset={handleReset}
            handleSelectionChange={handleSelectionChange}
            applyDisabled={isMemberCustomDateIncomplete}
            style={{ maxHeight: "unset" }}
          />
        </MuiDrawer>
      </div>

      <ActiveFilterBar
        chips={activeFilterChips}
        onRemove={handleRemoveFilter}
        onClearAll={handleReset}
      />
      <div id="user-list-container" className="col-span-12 mt-8">
        {/* Desktop Table View */}
        <TableContainer
          style={{ width: "100%", overflowX: "auto" }}
          className="hidden md:block"
        >
          <Table
            sx={{
              minWidth: "800px",
              borderCollapse: "separate",
              borderSpacing: "0px 8px",
            }}
            aria-label="simple table"
          >
            <TableHead className="bg-[#00ADEF]">
              <TableRow
                className="h-[50px]"
                sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
              >
                {columns.map((column, index) => (
                  <TruncatedTableCell
                    key={index}
                    className="!text-[#FFFFFF]"
                    textClassName="!text-[#FFFFFF] !font-[700]"
                    title={column.name}
                    maxChars={14}
                  >
                    {column.name}
                  </TruncatedTableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {allMembers &&
                allMembers.length > 0 &&
                allMembers.map((member, index) => (
                  <TableRow
                    key={index}
                    className="h-[50px]"
                    sx={{
                      boxShadow: "0px 4px 4px 0px #00000040",
                      backgroundColor:
                        (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                    }}
                  >
                    <TruncatedTableCell className="max-w-[60px]">
                      {(currentPage - 1) * 10 + index + 1}
                    </TruncatedTableCell>
                    <TruncatedTableCell
                      className="max-w-[160px]"
                      title={member?.name || "N/A"}
                    >
                      {truncateMemberName(member?.name)}
                    </TruncatedTableCell>
                    <TruncatedTableCell className="max-w-[120px]">
                      {formatDate(member?.createdAt)}
                    </TruncatedTableCell>
                    <TruncatedTableCell className="max-w-[180px]">
                      {member?.email || "N/A"}
                    </TruncatedTableCell>
                    <TruncatedTableCell className="max-w-[120px]">{`$${member?.subscriptionAmount || "0"}`}</TruncatedTableCell>
                    <TableCell className="max-w-[110px]">
                      {(() => {
                        const subscriptionBadge = getSubscriptionBadgeMeta(
                          member?.subscriptionStatus,
                        );

                        return (
                          <Tooltip
                            title={subscriptionBadge.label}
                            arrow
                            placement="top"
                          >
                            <div
                              className="flex items-center h-[24px] pl-2 pr-1.5 rounded-full border-[0.5px] select-none"
                              style={{
                                backgroundColor: subscriptionBadge.bgColor,
                                borderColor: subscriptionBadge.borderColor,
                                width: "112px",
                              }}
                            >
                              <div
                                className="flex items-center justify-center w-[14px] h-[14px] rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor: subscriptionBadge.dotColor,
                                }}
                              >
                                {subscriptionBadge.icon}
                              </div>
                              <span className="text-[11px] font-[400] ml-1.5 font-poppins text-[#303033] truncate flex-1">
                                {subscriptionBadge.label}
                              </span>
                            </div>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="max-w-[110px]">
                      {(() => {
                        const statusLower = String(
                          member?.status || "",
                        ).toLowerCase();
                        const isDeleted =
                          member?.isDeleted === true ||
                          statusLower === "deleted";
                        const isDisabled =
                          statusLower === "disabled" ||
                          statusLower === "rejected" ||
                          statusLower === "declined";
                        const isLive =
                          statusLower === "approved" || statusLower === "live";

                        const bgColor = isLive
                          ? "rgba(80, 205, 137, 0.15)"
                          : "rgba(236, 130, 110, 0.15)";
                        const borderColor = isLive ? "#50CD89" : "#EC826E";
                        const dotColor = isLive ? "#50CD89" : "#EC826E";

                        const label = isLive
                          ? "Live"
                          : isDeleted
                            ? "Deleted"
                            : "Disabled";

                        return (
                          <Dropdown
                            menu={{
                              items: [
                                !isLive && {
                                  key: "live",
                                  label: (
                                    <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                                      <div className="flex items-center justify-center w-[16px] h-[16px] rounded-full bg-[#50CD89]">
                                        <svg
                                          width="10"
                                          height="10"
                                          viewBox="8 6 14 14"
                                          fill="none"
                                        >
                                          <path
                                            d="M12 13.6667L14.3333 16L19 11"
                                            stroke="white"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </div>
                                      <span className="text-[13px] font-[400] font-poppins text-[#303033]">
                                        Live
                                      </span>
                                    </div>
                                  ),
                                  onClick: () =>
                                    handleStatusChangeRequest(
                                      member?._id,
                                      "live",
                                      member,
                                    ),
                                },
                                !isDeleted &&
                                  !isDisabled && {
                                    key: "disabled",
                                    label: (
                                      <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                                        <div className="flex items-center justify-center w-[16px] h-[16px] rounded-full bg-[#EC826E]">
                                          <svg
                                            width="10"
                                            height="10"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="white"
                                            strokeWidth="4"
                                            strokeLinecap="round"
                                          >
                                            <path d="M7 12h10" />
                                          </svg>
                                        </div>
                                        <span className="text-[13px] font-[400] font-poppins text-[#303033]">
                                          Disable
                                        </span>
                                      </div>
                                    ),
                                    onClick: () =>
                                      handleStatusChangeRequest(
                                        member?._id,
                                        "disabled",
                                        member,
                                      ),
                                  },
                                !isDeleted && {
                                  key: "deleted",
                                  label: (
                                    <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                                      <div className="flex items-center justify-center w-[16px] h-[16px] rounded-full bg-[#EC826E]">
                                        <svg
                                          width="10"
                                          height="10"
                                          viewBox="8 6 14 14"
                                          fill="none"
                                        >
                                          <path
                                            d="M18 15.8573C18 15.9358 17.9377 16 17.8616 16L16.7197 15.9946L15 13.8811L13.282 15.9929L12.1384 15.9982C12.0623 15.9982 12 15.9358 12 15.8555C12 15.8216 12.0121 15.7895 12.0329 15.7628L14.2837 12.9982L12.0329 10.2354C12.012 10.2093 12.0004 10.1766 12 10.1427C12 10.0642 12.0623 10 12.1384 10L13.282 10.0054L15 12.1189L16.718 10.0071L17.8599 10.0018C17.936 10.0018 17.9983 10.0642 17.9983 10.1445C17.9983 10.1784 17.9862 10.2105 17.9654 10.2372L15.718 13L17.9689 15.7646C17.9896 15.7895 18 15.8234 18 15.8573Z"
                                            fill="white"
                                          />
                                        </svg>
                                      </div>
                                      <span className="text-[13px] font-[400] font-poppins text-[#303033]">
                                        Delete
                                      </span>
                                    </div>
                                  ),
                                  onClick: () =>
                                    handleDeleteMember(member?._id),
                                },
                              ].filter(Boolean),
                            }}
                            trigger={["click"]}
                            placement="bottomLeft"
                            disabled={
                              isDeleted ||
                              statusChangeLoading ||
                              statusMutationLockRef.current
                            }
                            open={openMemberStatusDropdownId === member?._id}
                            onOpenChange={(nextOpen) =>
                              setOpenMemberStatusDropdownId(
                                nextOpen ? member?._id : null,
                              )
                            }
                          >
                            <span className="inline-flex">
                              <Tooltip
                                title={
                                  openMemberStatusDropdownId === member?._id
                                    ? null
                                    : label
                                }
                                arrow
                                placement="top"
                              >
                                <div
                                  className="cursor-pointer flex items-center h-[24px] pl-2 pr-1.5 rounded-full border-[0.5px] select-none"
                                  style={{
                                    backgroundColor: bgColor,
                                    borderColor,
                                    width: "98px",
                                    cursor:
                                      isDeleted ||
                                      statusChangeLoading ||
                                      statusMutationLockRef.current
                                        ? "not-allowed"
                                        : "default",
                                  }}
                                >
                                  <div
                                    className="flex items-center justify-center w-[14px] h-[14px] rounded-full flex-shrink-0"
                                    style={{ backgroundColor: dotColor }}
                                  >
                                    {isLive ? (
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="8 6 14 14"
                                        fill="none"
                                        style={{ display: "block" }}
                                      >
                                        <path
                                          d="M12 13.6667L14.3333 16L19 11"
                                          stroke="white"
                                          strokeWidth="2.0"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    ) : isDeleted ? (
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="8 6 14 14"
                                        fill="none"
                                        style={{ display: "block" }}
                                      >
                                        <path
                                          d="M18 15.8573C18 15.9358 17.9377 16 17.8616 16L16.7197 15.9946L15 13.8811L13.282 15.9929L12.1384 15.9982C12.0623 15.9982 12 15.9358 12 15.8555C12 15.8216 12.0121 15.7895 12.0329 15.7628L14.2837 12.9982L12.0329 10.2354C12.012 10.2093 12.0004 10.1766 12 10.1427C12 10.0642 12.0623 10 12.1384 10L13.282 10.0054L15 12.1189L16.718 10.0071L17.8599 10.0018C17.936 10.0018 17.9983 10.0642 17.9983 10.1445C17.9983 10.1784 17.9862 10.2105 17.9654 10.2372L15.718 13L17.9689 15.7646C17.9896 15.7895 18 15.8234 18 15.8573Z"
                                          fill="white"
                                        />
                                      </svg>
                                    ) : (
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="white"
                                        strokeWidth="4"
                                        strokeLinecap="round"
                                        style={{ display: "block" }}
                                      >
                                        <path d="M7 12h10" />
                                      </svg>
                                    )}
                                  </div>
                                  <span className="text-[11px] font-[400] ml-1.5 font-poppins text-[#303033] truncate flex-1 capitalize">
                                    {label}
                                  </span>
                                  {!isDeleted && (
                                    <div className="ml-1.5 flex items-center flex-shrink-0">
                                      {renderMemberChevron()}
                                    </div>
                                  )}
                                </div>
                              </Tooltip>
                            </span>
                          </Dropdown>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="w-[150px] max-w-[150px]">
                      <div className="flex items-center justify-center w-full">
                        <button
                          className="inline-flex justify-center items-center border border-[#00ADEF] rounded-[6px] px-3 h-[34px] hover:bg-[#00ADEF10] transition-colors whitespace-nowrap"
                          onClick={() => openPopup(member?._id)}
                        >
                          <span className="font-[600] text-[14px] font-poppins text-[#00ADEF] leading-[22px]">
                            View Details
                          </span>
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

              {!loading &&
                !searching &&
                allMembers &&
                allMembers.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      className="!border-b-0 !p-0"
                    >
                      <TableEmptyState itemName="Member" />
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Mobile Card View */}
        <div className="block md:hidden space-y-4">
          {allMembers &&
            allMembers.length > 0 &&
            allMembers.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-[10px] p-4 border border-[#E0E0E0] shadow-sm flex flex-col gap-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3
                      className="text-[16px] font-[600] text-[#303030] font-poppins truncate max-w-[200px]"
                      title={member?.name || "N/A"}
                    >
                      {truncateMemberName(member?.name)}
                    </h3>
                    <p className="text-[12px] font-[400] text-[#767676] font-poppins">
                      {member?.email || "N/A"}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {(() => {
                      const subscriptionBadge = getSubscriptionBadgeMeta(
                        member?.subscriptionStatus,
                      );

                      return (
                        <Tooltip
                          title={subscriptionBadge.label}
                          arrow
                          placement="top"
                        >
                          <div
                            className="flex items-center h-[24px] pl-2 pr-1.5 rounded-full border-[0.5px] select-none"
                            style={{
                              backgroundColor: subscriptionBadge.bgColor,
                              borderColor: subscriptionBadge.borderColor,
                              width: "110px",
                            }}
                          >
                            <div
                              className="flex items-center justify-center w-[14px] h-[14px] rounded-full flex-shrink-0"
                              style={{
                                backgroundColor: subscriptionBadge.dotColor,
                              }}
                            >
                              {subscriptionBadge.icon}
                            </div>
                            <span className="text-[10px] font-[400] ml-1.5 font-poppins text-[#303033] truncate flex-1">
                              {subscriptionBadge.label}
                            </span>
                          </div>
                        </Tooltip>
                      );
                    })()}

                    <Tooltip
                      title={(() => {
                        const s = String(member?.status || "").toLowerCase();
                        const isDeleted =
                          member?.isDeleted === true || s === "deleted";
                        if (s === "approved" || s === "live") return "Live";
                        if (isDeleted) return "Deleted";
                        return "Disabled";
                      })()}
                      arrow
                      placement="top"
                    >
                      <div
                        className="flex items-center h-[24px] pl-2 pr-1.5 rounded-full border-[0.5px] select-none"
                        style={{
                          backgroundColor: (() => {
                            const s = String(
                              member?.status || "",
                            ).toLowerCase();
                            const isDeleted =
                              member?.isDeleted === true || s === "deleted";
                            if (s === "approved" || s === "live")
                              return "rgba(80, 205, 137, 0.15)";
                            if (isDeleted) return "rgba(236, 130, 110, 0.15)";
                            return "rgba(236, 130, 110, 0.15)";
                          })(),
                          borderColor: (() => {
                            const s = String(
                              member?.status || "",
                            ).toLowerCase();
                            const isDeleted =
                              member?.isDeleted === true || s === "deleted";
                            if (s === "approved" || s === "live")
                              return "#50CD89";
                            if (isDeleted) return "#EC826E";
                            return "#EC826E";
                          })(),
                          width: "110px",
                        }}
                      >
                        <div
                          className="flex items-center justify-center w-[14px] h-[14px] rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: (() => {
                              const s = String(
                                member?.status || "",
                              ).toLowerCase();
                              const isDeleted =
                                member?.isDeleted === true || s === "deleted";
                              if (s === "approved" || s === "live")
                                return "#50CD89";
                              if (isDeleted) return "#EC826E";
                              return "#EC826E";
                            })(),
                          }}
                        >
                          {String(member?.status || "").toLowerCase() ===
                            "approved" ||
                          (member?.status || "").toLowerCase() === "live" ? (
                            <svg
                              width="10"
                              height="10"
                              viewBox="8 6 14 14"
                              fill="none"
                              style={{ display: "block" }}
                            >
                              <path
                                d="M12 13.6667L14.3333 16L19 11"
                                stroke="white"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : member?.isDeleted === true ||
                            String(member?.status || "").toLowerCase() ===
                              "deleted" ? (
                            <svg
                              width="10"
                              height="10"
                              viewBox="8 6 14 14"
                              fill="none"
                              style={{ display: "block" }}
                            >
                              <path
                                d="M18 15.8573C18 15.9358 17.9377 16 17.8616 16L16.7197 15.9946L15 13.8811L13.282 15.9929L12.1384 15.9982C12.0623 15.9982 12 15.9358 12 15.8555C12 15.8216 12.0121 15.7895 12.0329 15.7628L14.2837 12.9982L12.0329 10.2354C12.012 10.2093 12.0004 10.1766 12 10.1427C12 10.0642 12.0623 10 12.1384 10L13.282 10.0054L15 12.1189L16.718 10.0071L17.8599 10.0018C17.936 10.0018 17.9983 10.0642 17.9983 10.1445C17.9983 10.1784 17.9862 10.2105 17.9654 10.2372L15.718 13L17.9689 15.7646C17.9896 15.7895 18 15.8234 18 15.8573Z"
                                fill="white"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="white"
                              strokeWidth="4"
                              strokeLinecap="round"
                              style={{ display: "block" }}
                            >
                              <path d="M7 12h10" />
                            </svg>
                          )}
                        </div>
                        <span className="text-[10px] font-[400] ml-1.5 font-poppins text-[#303033] truncate flex-1 capitalize">
                          {(() => {
                            const s = String(
                              member?.status || "",
                            ).toLowerCase();
                            const isDeleted =
                              member?.isDeleted === true || s === "deleted";
                            if (s === "approved" || s === "live") return "Live";
                            if (isDeleted) return "Deleted";
                            return "Disabled";
                          })()}
                        </span>
                      </div>
                    </Tooltip>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-[13px] font-poppins text-[#303030]">
                  <div className="flex flex-col">
                    <span className="text-[#8C8C8C] text-[11px]">
                      Joined Date
                    </span>
                    <span className="truncate">
                      {formatDate(member?.createdAt)}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[#8C8C8C] text-[11px]">
                      Sub. Amount
                    </span>
                    <span className="truncate font-[600] text-[#00ADEF]">
                      ${member?.subscriptionAmount || "0"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    className="w-full flex justify-center items-center border border-[#00ADEF] rounded-[6px] py-2 bg-[#F0FBFF] hover:bg-[#E0F7FA] transition-colors"
                    onClick={() => openPopup(member?._id)}
                  >
                    <span className="font-[600] text-[13px] text-[#00ADEF] font-poppins">
                      View Details
                    </span>
                  </button>
                </div>
              </div>
            ))}
          {!loading &&
            !searching &&
            (!allMembers || allMembers.length === 0) && (
              <TableEmptyState itemName="Member" />
            )}
        </div>

        {allMembers && allMembers.length > 0 && (
          <Pagination
            totalPages={totalPages}
            handlePageChange={handlePageChange}
          />
        )}
      </div>
    </>
  );
};

export default MemberListDataTable;
